import '../styles/PdfToolbar.scss';
import React, { useRef, useCallback, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import { isMobile, isIos } from '@umu/umu-ua';
import { SearchPdfTool } from '../components/searchPdfToolBar';
import { ISearchPdfToolBarRef } from '../components/searchPdfToolBar/interface';
import FlipModeMenu from '../components/flipModeMenu';
import { PageSelect } from '../components/pageSleect';
import { ZoomSelect } from '../components/zoomSelect';
import { FullScreenSelect } from '../components/fullScreenSelect';
import { usePdfToolbar } from '../hooks/usePdfToolbar';
import { IStaticInfo, PageFlipMode, SCREEN_STATE } from '../types';
import { useEventListener } from 'ahooks';

export interface PdfToolbarProps {
    currentPage: number;
    totalPages: number;
    scale: number;
    screenState: SCREEN_STATE;
    isShowBar: boolean;
    loading: boolean;
    allowSearch?: boolean;
    pageFlipMode: PageFlipMode;
    showOutlines: boolean;
    isMobileShowSearchDrawer: boolean;
    setIsMobileShowSearchDrawer: (isShow: boolean) => void;
    onZoomIn: () => void;
    onZoomOut: () => void;
    onPageJump: (page: number) => void;
    onFullScreenToggle: () => void;
    onFlipModeChange: (mode: PageFlipMode) => void;
    onSearch: (value: string) => Promise<IStaticInfo[]>;
    onSearchClose: VoidFunction;
    hideToolbar: VoidFunction;
    showToolbar: VoidFunction;
    onJumpToElement: (element: IStaticInfo) => Promise<boolean>;
}

export const PdfToolbar = React.memo((props: PdfToolbarProps) => {
    const {
        currentPage: displayPageNum,
        totalPages,
        scale,
        screenState,
        isShowBar,
        loading,
        allowSearch,
        pageFlipMode,
        showOutlines,
        isMobileShowSearchDrawer,
        setIsMobileShowSearchDrawer,
        onZoomIn,
        onZoomOut,
        onPageJump,
        onFullScreenToggle,
        onFlipModeChange,
        onSearch,
        onSearchClose,
        showToolbar,
        onJumpToElement,
    } = props;

    const toolbarSearchRef = useRef<ISearchPdfToolBarRef>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    // 非全屏点击搜索时，延迟开启搜索状态直到全屏 DOM 稳定，防止 iOS 键盘被 blur
    const pendingSearchOpenRef = useRef(false);
    const isFullScreen = useMemo(() => screenState === 'full', [screenState]);
    const isShowToolbar = useMemo(() => isShowBar || isMobileShowSearchDrawer, [isShowBar, isMobileShowSearchDrawer]);
    const hideTool = useMemo(() => isMobile && isMobileShowSearchDrawer, [isMobileShowSearchDrawer]);

    const {
        toolbarRef,
        currentPageText,
        isInputting,
        toolbarHeight,
        toolbarStyle,
        onInputFocus,
        onInputBlur,
        onInputKeyDown,
        onInputPaste,
        onPageInputClick,
    } = usePdfToolbar({
        currentPage: displayPageNum,
        totalPages,
        screenState,
        showOutlines,
        isToolbarVisible: isShowToolbar,
        onPageJump,
    });

    const shouldPreventSelection = useCallback((target: HTMLElement) => {
        // selectstart/select 事件的 target 可能是 Text 节点，不具备 closest()
        if (!(target instanceof Element)) return true;
        return !(
            target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.isContentEditable ||
            target.closest('input') ||
            target.closest('textarea') ||
            inputRef.current?.contains(target)
        );
    }, []);

    const handlePreventSelection = useCallback(
        (e: React.MouseEvent | React.TouchEvent) => {
            if (shouldPreventSelection(e.target as HTMLElement)) {
                e.preventDefault();
            }
        },
        [shouldPreventSelection],
    );

    useEventListener(
        'selectstart',
        (e: Event) => {
            if (shouldPreventSelection(e.target as HTMLElement)) {
                e.preventDefault();
            }
        },
        { target: toolbarRef },
    );

    useEventListener(
        'select',
        (e: Event) => {
            if (shouldPreventSelection(e.target as HTMLElement)) {
                e.preventDefault();
                const selection = window.getSelection();
                if (selection) {
                    selection.removeAllRanges();
                }
            }
        },
        { target: toolbarRef },
    );

    // PC 端：全屏切换前主动关闭搜索下拉面板，避免全屏 DOM 重构后面板位置错位
    // （reactive 的 useUpdateLayoutEffect 方案中 Dropdown 可能在 layout effect 之前已重定位）
    const handleFullScreenToggle = useCallback(() => {
        if (!isMobile) {
            toolbarSearchRef.current?.close(false);
        }
        onFullScreenToggle();
    }, [onFullScreenToggle]);

    const onBeforeShowSearchDrawer = useCallback(
        (showSearchDrawer: boolean) => {
            if (!showSearchDrawer) {
                // 关闭时：取消任何待处理的搜索打开意图，恢复工具栏（如有需要）
                pendingSearchOpenRef.current = false;
                if (!isShowBar) showToolbar();
                setIsMobileShowSearchDrawer(false);
                return;
            }
            if (isMobile && !isFullScreen) {
                // 非全屏时点击搜索：仅触发全屏，暂不更新 isMobileShowSearchDrawer。
                // 全屏 DOM 重构会强制 blur 已聚焦的 input，若同步设置 isMobileShowSearchDrawer
                // 则两处 DOM 变化叠加，iOS 键盘更难恢复。
                // 改为等 isFullScreen 变 true（全屏 DOM 稳定）后，
                // 再由下方 useEffect 统一开启搜索状态并聚焦。
                pendingSearchOpenRef.current = true;
                handleFullScreenToggle();
                return;
            }
            setIsMobileShowSearchDrawer(true);
        },
        [isShowBar, isFullScreen, handleFullScreenToggle, showToolbar],
    );

    // iOS 搜索聚焦：处理两种场景
    // 场景1（非全屏→全屏）：pendingSearchOpenRef 标记 isFullScreen 变 true 时触发，
    //   在全屏 DOM 稳定后统一设置 isMobileShowSearchDrawer 并聚焦。
    // 场景2（已全屏）：isMobileShowSearchDrawer 变 true 时直接触发 rAF 聚焦。
    // 两种场景均在 DOM commit 后通过 rAF 聚焦，相比原"全屏与搜索状态同步触发"的方案，
    // 减少了 iOS focus() 时的并发 DOM 变化，键盘弹出更可靠。
    useEffect(() => {
        if (!isMobile) return undefined;
        if (pendingSearchOpenRef.current && isFullScreen) {
            // 场景1：全屏已就绪，现在开启搜索状态并聚焦
            pendingSearchOpenRef.current = false;
            setIsMobileShowSearchDrawer(true);
            const rafId = requestAnimationFrame(() => {
                toolbarSearchRef.current?.onFocus?.();
            });
            return () => cancelAnimationFrame(rafId);
        }
        if (isMobileShowSearchDrawer && isFullScreen) {
            // 场景2：已全屏直接打开搜索，或全屏状态下搜索状态更新后的聚焦恢复
            const rafId = requestAnimationFrame(() => {
                toolbarSearchRef.current?.onFocus?.();
            });
            return () => cancelAnimationFrame(rafId);
        }
        return undefined;
    }, [isMobileShowSearchDrawer, isFullScreen]);

    const contentStyle: React.CSSProperties = useMemo(() => {
        return {
            height: isFullScreen ? toolbarHeight || 'auto' : isShowToolbar ? toolbarHeight || 'auto' : '0px',
            overflow: isMobileShowSearchDrawer || isShowBar ? 'visible' : 'hidden',
            position: 'relative',
            zIndex: isFullScreen ? 1200 : 600
        };
    }, [isFullScreen, isShowToolbar, toolbarHeight, isMobileShowSearchDrawer, isShowBar]);
    const containerStyle: any = useMemo(() => {
        return {
            ...toolbarStyle,
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
        };
    }, [toolbarStyle]);
    return (
        <div
            className={classNames('umu-pdf-version-toolbar', {
                'umu-pdf-version-toolbar--hide': !isShowToolbar,
                'umu-pdf-version-toolbar--fullscreen': isFullScreen,
            })}
            style={contentStyle}
        >
            <div
                ref={toolbarRef}
                className={classNames('umu-pdf-version-toolbar-container umu-pdf-ignore-rem ignore-transform-to-rem', {
                    'umu-pdf-version-toolbar-container--fullscreen': isFullScreen,
                    'umu-pdf-version-toolbar-container--mobile-ios': isMobile && isIos,
                    'umu-pdf-version-toolbar-container--hide': !isShowToolbar,
                    'umu-pdf-version-toolbar-container--loading': loading,
                    'umu-pdf-version-toolbar-container--mobile': isMobile,
                    'umu-pdf-version-toolbar-container--mobile-search': hideTool,
                })}
                style={containerStyle}
                onMouseDown={handlePreventSelection}
            >
                <FullScreenSelect screenState={screenState} onFullScreenToggle={handleFullScreenToggle} />
                <div className="umu-pdf-toolbar-section-center">
                    <ZoomSelect scale={scale} onZoomIn={onZoomIn} onZoomOut={onZoomOut} />
                    <PageSelect
                        displayPageNum={displayPageNum}
                        totalPages={totalPages}
                        onPageJump={onPageJump}
                        isInputting={isInputting}
                        inputRef={inputRef}
                        onInputBlur={onInputBlur}
                        onInputFocus={onInputFocus}
                        onInputKeyDown={onInputKeyDown}
                        onInputPaste={onInputPaste}
                        onPageInputClick={onPageInputClick}
                        currentPageText={currentPageText}
                    />
                </div>
                <div className="umu-pdf-toolbar-section-right">
                    <FlipModeMenu
                        className={classNames({
                            'toolbar-flip-mode-hidden': !isFullScreen || (isMobileShowSearchDrawer && isMobile),
                        })}
                        pageFlipMode={pageFlipMode}
                        screenState={screenState}
                        onFlipModeChange={onFlipModeChange}
                    />

                    {allowSearch && (
                        <div
                            className={classNames('umu-pdf-toolbar-search-box', {
                                'umu-pdf-toolbar-search-box--full': isFullScreen,
                                'mobile-search': isMobile,
                            })}
                        >
                            <SearchPdfTool
                                ref={toolbarSearchRef}
                                onSearch={onSearch}
                                onClose={onSearchClose}
                                currentPage={displayPageNum}
                                onJumpToElement={onJumpToElement}
                                onBeforeShowSearchDrawer={onBeforeShowSearchDrawer}
                                screenState={screenState}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});
